import React, { useState, useEffect } from 'react';
import { t } from '../../utils/i18n';
import { hasRole } from '../../utils/roleHelper';

const DailyAttendancePage = ({ api, user }) => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [stats, setStats] = useState({ present: 0, absent: 0, late: 0, onLeave: 0 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [departments, setDepartments] = useState([]);

  const isAdmin = hasRole(user, 'admin');

  useEffect(() => {
    fetchDepartments();
    fetchDailyAttendance();
  }, [date, departmentFilter]);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response || []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchDailyAttendance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date });
      if (departmentFilter) params.append('department_id', departmentFilter);

      const response = await api.get(`/attendance_summaries?${params}`);
      const summaries = response.attendance_summaries || [];

      setAttendanceData(summaries);
      calculateStats(summaries);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const present = data.filter(d => d.status === 'present').length;
    const absent = data.filter(d => d.status === 'absent').length;
    const late = data.filter(d => d.late_minutes > 0).length;
    setStats({ present, absent, late, onLeave: 0 });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const filteredData = attendanceData.filter(record => {
    const armyId = record.army_personnel?.army_id || record.army_personnel?.user?.army_id || record.army_personnel_id?.toString() || '';
    const name = record.army_personnel?.full_name || '';
    return armyId.toLowerCase().includes(searchTerm.toLowerCase()) || name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="contentArea">
      <div className="dFlex baseTableHd mb_24">
        <h1 className="txt28 fwBold colorBlack">Daily Attendance</h1>
        <button className="btnAdd me-3" onClick={fetchDailyAttendance}>
          <img src="/images/refresh.png" alt="" style={{ width: '16px', marginRight: '8px' }} />
          Refresh
        </button>
      </div>

      <div className="dFlex" style={{ gap: '16px', marginBottom: '24px' }}>
        <div className="dashFeaturesBox" style={{ flex: 1 }}>
          <div className="dashFeaturesTxt">
            Present
            <span className="txt30 fwBold" style={{ color: '#28a745' }}>{stats.present}</span>
          </div>
        </div>
        <div className="dashFeaturesBox" style={{ flex: 1 }}>
          <div className="dashFeaturesTxt">
            Absent
            <span className="txt30 fwBold" style={{ color: '#dc3545' }}>{stats.absent}</span>
          </div>
        </div>
        <div className="dashFeaturesBox" style={{ flex: 1 }}>
          <div className="dashFeaturesTxt">
            Late
            <span className="txt30 fwBold" style={{ color: '#ffc107' }}>{stats.late}</span>
          </div>
        </div>
        <div className="dashFeaturesBox" style={{ flex: 1 }}>
          <div className="dashFeaturesTxt">
            On Leave
            <span className="txt30 fwBold" style={{ color: '#17a2b8' }}>{stats.onLeave}</span>
          </div>
        </div>
      </div>

      <div className="baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <div className="dFlex" style={{ gap: '12px' }}>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="form-control"
              style={{ width: '200px' }}
            />
            {isAdmin && (
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="form-control"
                style={{ width: '200px' }}
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="searchBox">
            <input
              type="text"
              placeholder="Search by Personnel ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="button">
              <img src="/images/search1.png" alt="" />
            </button>
          </div>
        </div>

        <div className="baseTableContainer">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
          ) : (
            <table className="baseTable">
              <thead>
                <tr>
                  <th>Personnel ID</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Work Duration</th>
                  <th>Late (mins)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                      No attendance records found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((record) => (
                    <tr key={record.id}>
                      <td>{record.army_personnel?.army_id || record.army_personnel?.user?.army_id || record.army_personnel_id}</td>
                      <td>{formatTime(record.first_check_in)}</td>
                      <td>{formatTime(record.last_check_out)}</td>
                      <td>{formatDuration(record.worked_seconds)}</td>
                      <td>
                        <span style={{ color: record.late_minutes > 0 ? '#dc3545' : '#28a745' }}>
                          {record.late_minutes || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${record.status === 'present' ? 'green' : 'red'}`}>
                          {record.status || 'absent'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyAttendancePage;
